// Save the WHOLE drip sequence (every message + the gaps between them) as a
// reusable `drip-templates` row (FE#331). Self-contained overlay so both
// surfaces that render the drip action menu (the list card + the detail header)
// can drop it in with a single line. Applying a saved template lives in
// `drip-template-picker.tsx`.
import React from 'react';
import { toastDBCreateError } from '@common/utils/db-create-error-toast';
import { X, Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBDripTemplateThunks } from '@common-reducers/DBServiceThunks';
import { buildDripTemplatePayload } from '@common-reducers/drip-template-helpers';
import type { Drip } from '@bt-common/schemas/bt/drip';
import type { DripTemplate } from '@common/models/drip-template';
import { gapText, type DelayGap } from './primitives';

interface SaveAsDripTemplateModalProps {
  open: boolean;
  /** The drip whose steps + gaps are being saved. */
  drip?: Drip;
  onClose: () => void;
  /** Fired after a template is created (surfaces may toast/confirm). */
  onSaved?: (template: DripTemplate) => void;
}

export const SaveAsDripTemplateModal: React.FC<SaveAsDripTemplateModalProps> = ({
  open,
  drip,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [name, setName] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Reset the name each time the modal opens for a (possibly different) drip.
  React.useEffect(() => {
    if (open) setName(drip?.name ? `${drip.name}` : '');
  }, [open, drip?.name]);

  if (!open || !drip) return null;

  const steps = drip.steps ?? [];
  const canSave = name.trim().length > 0 && steps.length > 0 && !saving;

  const doSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = buildDripTemplatePayload(drip, name);
      const created = await dispatch(DBDripTemplateThunks.create(payload as DripTemplate)).unwrap();
      onSaved?.(created as DripTemplate);
      onClose();
    } catch (err) {
      console.error('[SaveAsDripTemplateModal] create failed', err);
      // #1136: the create now rejects when nothing was stored; the modal stays open.
      toastDBCreateError(err, 'Your drip template was not saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', zIndex: 2147483000, padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(460px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,.28)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}>
          <strong style={{ fontSize: 14, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bookmark size={15} /> {t('blueticksPanel.dripTemplates.saveModalTitle', 'Save as drip template')}
          </strong>
          <button type="button" aria-label={t('common.close', 'Close')} onClick={onClose} className="btn ghost" style={{ padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            {t('blueticksPanel.dripTemplates.saveModalSubtitle', 'Save this whole sequence — every message and the gaps between them — so you can reuse it for another group later.')}
          </p>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 600 }}>
            {t('blueticksPanel.dripTemplates.nameLabel', 'Template name')}
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void doSave(); }}
              placeholder={t('blueticksPanel.dripTemplates.namePlaceholder', 'e.g. 3-step welcome follow-up')}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }}
            />
          </label>

          <div style={{ border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)', borderBottom: '1px solid var(--line)' }}>
              {t('blueticksPanel.dripTemplates.stepCount', '{{count}} messages', { count: steps.length })}
            </div>
            <ol style={{ margin: 0, padding: '6px 0', listStyle: 'none' }}>
              {steps.map((s, i) => (
                <li key={i} style={{ padding: '5px 12px', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ minWidth: 18, height: 18, borderRadius: 5, background: 'var(--surface-3, var(--line))', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700 }}>{i + 1}</span>
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(s.template as { message?: string })?.message?.trim() || t('blueticksPanel.dripTemplates.mediaStep', 'Media / poll message')}
                    </span>
                  </span>
                  {s.gapAfter && (
                    <span style={{ marginInlineStart: 24, fontSize: 11, color: 'var(--muted)' }}>
                      {t('blueticksPanel.dripTemplates.thenWait', 'then wait {{gap}}', { gap: gapText(t, s.gapAfter as DelayGap) })}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--line)', background: 'var(--surface)' }}>
          <button type="button" className="btn ghost" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
          <button type="button" className="btn primary" disabled={!canSave} onClick={() => void doSave()}>
            {t('blueticksPanel.dripTemplates.save', 'Save template')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveAsDripTemplateModal;
